import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import React, { FC, MouseEvent, useState } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";

import { AdvertiserType } from "../../../../types/advertiserTypes";

type AdvertiserRowActionsMenuProps = {
  advertiser: AdvertiserType;
  onEditPaymentMethod: (advertiser: AdvertiserType) => void;
  onHubspotConnect: (advertiser: AdvertiserType) => void;
  onHubspotDisconnect: (advertiser: AdvertiserType) => void;
  onArchive: (advertiser: AdvertiserType) => void;
  onUnarchive: (advertiser: AdvertiserType) => void;
};

const MoreButton = styled("button")(({ theme }: { theme: Theme }) => ({
  background: "none",
  border: "none",
  cursor: "pointer",
  color: theme.palette.brand.gray400,
  padding: 4,
  borderRadius: 6,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  "&:hover": {
    background: theme.palette.brand.gray100,
    color: theme.palette.brand.gray700,
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }: { theme: Theme }) => ({
  fontSize: 13,
  fontWeight: 500,
  color: theme.palette.brand.gray700,
  minWidth: 200,
  "&:hover": {
    background: theme.palette.brand.gray50,
  },
}));

const DangerMenuItem = styled(StyledMenuItem)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.errorRed,
}));

const MoreIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="1" />
    <circle cx="19" cy="12" r="1" />
    <circle cx="5" cy="12" r="1" />
  </svg>
);

const AdvertiserRowActionsMenu: FC<AdvertiserRowActionsMenuProps> = ({
  advertiser,
  onEditPaymentMethod,
  onHubspotConnect,
  onHubspotDisconnect,
  onArchive,
  onUnarchive,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?: MouseEvent) => {
    event?.stopPropagation();
    setAnchorEl(null);
  };

  const handlePaymentMethod = (event: MouseEvent) => {
    event.stopPropagation();
    handleClose();
    onEditPaymentMethod(advertiser);
  };

  const handleHubspotConnect = (event: MouseEvent) => {
    event.stopPropagation();
    handleClose();
    onHubspotConnect(advertiser);
  };

  const handleHubspotDisconnect = (event: MouseEvent) => {
    event.stopPropagation();
    handleClose();
    onHubspotDisconnect(advertiser);
  };

  const handleArchive = (event: MouseEvent) => {
    event.stopPropagation();
    handleClose();
    onArchive(advertiser);
  };

  const handleUnarchive = (event: MouseEvent) => {
    event.stopPropagation();
    handleClose();
    onUnarchive(advertiser);
  };

  const hasCard = Boolean(advertiser.paymentMethod?.card?.last4);

  return (
    <>
      <MoreButton type="button" aria-label={`Actions for ${advertiser.name}`} onClick={handleOpen}>
        <MoreIcon />
      </MoreButton>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={(event: object) => handleClose(event as MouseEvent)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        getContentAnchorEl={null}
      >
        <StyledMenuItem onClick={handlePaymentMethod}>
          {hasCard ? "Edit payment method" : "Add payment method"}
        </StyledMenuItem>
        {advertiser.hubspot_authed ? (
          <>
            <StyledMenuItem onClick={handleHubspotConnect}>Update HubSpot connection</StyledMenuItem>
            <DangerMenuItem onClick={handleHubspotDisconnect}>Disconnect HubSpot</DangerMenuItem>
          </>
        ) : (
          <StyledMenuItem onClick={handleHubspotConnect}>Connect HubSpot</StyledMenuItem>
        )}
        {advertiser.archived ? (
          <StyledMenuItem onClick={handleUnarchive}>Unarchive</StyledMenuItem>
        ) : (
          <DangerMenuItem onClick={handleArchive}>Archive</DangerMenuItem>
        )}
      </Menu>
    </>
  );
};

export default AdvertiserRowActionsMenu;
